import { View, FlatList,  Alert } from 'react-native'
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'

import styles from './Actualite.styles'
import { isWeb } from '@commonStyle';
import { Dialog } from '../../components/Dialog';
import { LoadActualite } from '../../api/Actualite/types'
import { ActualiteApi } from '../../api/Actualite'
import { DocumentApi } from '../../api/Document'
import { DocumentTypes } from '../../api/Document/types'

import { InfoCentre } from '../../components/InfoCentre/InfoCentre.component'
import { PdfViewer } from '../../components/PdfViewer'
import { getMimeTypeFromExtension } from '../../lib/generique'
import ActualiteItem from './ActualiteItem'
import ModalActualite from './ModalActualite';
import dayjs from 'dayjs';
import { usePolling } from '../../hooks/usePolling/usePolling.hook';
import { useNotify } from '../../hooks/useNotify/useNotify';

const defaultFile: {uri: string, name: string, extension?: string} = {uri: '', name: '', extension: ''}

export const Actualite: FunctionComponent = () => {
  const [selectedActualite, setSelectedActualite] = useState<LoadActualite>();
  const [actualites, setActualites] = useState<LoadActualite[]>([]);
  const [openedFile, setOpenedFile] = useState(defaultFile)
  const notify = useNotify();
  const actualitesRef = useRef<LoadActualite[]>([]);

  const handleLireDetails = useCallback((actualite: any) => {
    setSelectedActualite(actualite);
  }, []);

  const handleCloseOpenedFile = useCallback(() => setOpenedFile(defaultFile), []);

  const handleModalClose = useCallback(() => {
    setSelectedActualite(undefined);
  }, [])

  // Fonction de téléchargement du fichier
  const handleOpenFile = useCallback(async (actualite: LoadActualite) => {
    if (!actualite?.file) {
      Alert.alert('Erreur', 'Aucun fichier disponible pour le téléchargement');
      return;
    }

    const { extension } = actualite.documents?.find((doc) => doc.type === "ActualiteDoc") || {}

    // Utiliser le libellé de l'actualité comme nom de fichier
    const name = actualite.libelle 
      ? `${actualite.libelle.replace(/[^a-zA-Z0-9]/g, '_')}`
      : `actualite_${actualite.identifiant}_document`;
    
    setOpenedFile({uri: actualite.file, name, extension})
  }, []);

  const handleOpenCurrentFile = useCallback(() => {
    if(selectedActualite){
      handleOpenFile(selectedActualite)
    }
  }, [handleOpenFile, selectedActualite])

  // Fonction réutilisable pour charger et enrichir les actualités
  const loadAndEnrichActualites = useCallback(async (silent = false): Promise<LoadActualite[] | null> => {
    const response = await ActualiteApi.loadActualite(`?withDoc=1&dateRef=${dayjs().format('YYYY-MM-DD')}`, silent)
    if (!response || !Array.isArray(response)) return null

    const actuPromises = response.map(async (actualite: LoadActualite) => {
      // Réutiliser l'existant si déjà chargé (évite de re-télécharger les fichiers)
      const existing = actualitesRef.current.find(a => a.identifiant === actualite.identifiant)
      if (existing && silent) return existing

      const idImage = actualite.documents?.find(({type}: any) => type === DocumentTypes.IMG_ACTU)?.identifiant
      const idDoc = actualite.documents?.find(({type}: any) => type === DocumentTypes.DOC_ACTU)?.identifiant

      let actu: any = { ...actualite, documentId: idDoc }

      const [img, file] = await Promise.all([
        idImage ? DocumentApi.downloadDocument(idImage, silent) : Promise.resolve(undefined),
        idDoc ? DocumentApi.downloadDocument(idDoc, silent) : Promise.resolve(undefined)
      ])

      if (img) actu = { ...actu, img }
      if (file) actu = { ...actu, file }
      return actu
    })

    return Promise.all(actuPromises)
  }, [])

  // Chargement initial
  useEffect(() => { 
    const loadActualites = async () => {
      try {
        const actus = await loadAndEnrichActualites(false)
        if (actus) {
          setActualites(actus)
          actualitesRef.current = actus
        } else {
          setActualites([])
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement des actualités:', error);
        setActualites([]);
      } 
    };

    loadActualites();
  }, [loadAndEnrichActualites]);

  // Polling silencieux : compare les données brutes de l'API (pas seulement les IDs)
  usePolling<LoadActualite[]>({
    fetchFn: async () => {
      const response = await ActualiteApi.loadActualite(`?withDoc=1&dateRef=${dayjs().format('YYYY-MM-DD')}`, true)
      if (!response || !Array.isArray(response)) return null
      return response
    },
    enabled: true,
    hasChanged: (prev, next) => {
      if (prev.length !== next.length) return true
      // Comparer identifiants + libellé + description + date pour détecter toute modification
      return prev.some((a, i) => 
        a.identifiant !== next[i]?.identifiant ||
        a.libelle !== next[i]?.libelle ||
        a.description !== next[i]?.description ||
        a.date !== next[i]?.date ||
        a.contenu !== next[i]?.contenu
      )
    },
    onChanged: async (nextRaw) => {
      try {
        // Enrichir uniquement les nouvelles actualités (réutiliser les existantes)
        const actuPromises = nextRaw.map(async (actualite: LoadActualite) => {
          const existing = actualitesRef.current.find(a => a.identifiant === actualite.identifiant)
          if (existing) {
            // Mettre à jour les métadonnées mais garder les fichiers déjà téléchargés
            return { ...existing, libelle: actualite.libelle, description: actualite.description, date: actualite.date, contenu: actualite.contenu }
          }

          const idImage = actualite.documents?.find(({type}: any) => type === DocumentTypes.IMG_ACTU)?.identifiant
          const idDoc = actualite.documents?.find(({type}: any) => type === DocumentTypes.DOC_ACTU)?.identifiant

          let actu: any = { ...actualite, documentId: idDoc }

          const [img, file] = await Promise.all([
            idImage ? DocumentApi.downloadDocument(idImage, true) : Promise.resolve(undefined),
            idDoc ? DocumentApi.downloadDocument(idDoc, true) : Promise.resolve(undefined)
          ])

          if (img) actu = { ...actu, img }
          if (file) actu = { ...actu, file }
          return actu
        })

        const actus = await Promise.all(actuPromises)
        setActualites(actus)
        actualitesRef.current = actus
        notify.info('Les actualités ont été mises à jour', 3000)
      } catch {
        // Silencieux
      }
    },
  })

  return (   
    <>
      {openedFile.uri && (
        <>
          {isWeb
            ? <Dialog visible={isWeb} title={openedFile.name} onClose={handleCloseOpenedFile}>
                <PdfViewer
                  source={{uri: openedFile.uri}}
                  filename={`${openedFile.name}${openedFile.extension}`}          
                  onError={(pdfError) => {
                    console.log('Erreur PdfViewer:', pdfError);
                    setOpenedFile(defaultFile);
                  }}
                  mimeType={getMimeTypeFromExtension(openedFile.extension)}
                />
              </Dialog>
            : <PdfViewer
              source={{uri: openedFile.uri}}
              filename={`${openedFile.name}${openedFile.extension}`}          
              onError={(pdfError) => {
                console.log('Erreur PdfViewer:', pdfError);
              }}
              onFinally={() => setOpenedFile(defaultFile)}
              mimeType={getMimeTypeFromExtension(openedFile.extension)}
            />
          }
          </>
       
      )}    
      { actualites.length === 0 ? (
        <View style={styles.container}>
          <InfoCentre icon="rss-box" message="Aucune actualité n'est actuellement disponible"/>
        </View>
      ) : (                                
        <FlatList
          data={actualites}
          keyExtractor={(item) => item.identifiant.toString()}
          renderItem={({ item, index }) => <ActualiteItem index={index} item={item} onLireDetails={handleLireDetails} onFichierJoint={() => handleOpenFile(item)} />}  
          testID='SC_MAIN_CONTENT'
        />
      )}
      
      {selectedActualite && (
        <ModalActualite
          actualite={selectedActualite}
          onOpenFile={handleOpenCurrentFile}
          onClose={handleModalClose}
        />
      )}
    </>           
);
}

